/**
 * Supplier invoice worklist — `/finance/supplier-invoices`.
 *
 * The list is a keyset cursor page ordered by invoice date descending, and the
 * cursor binds the active filters, so changing a filter starts a fresh page
 * sequence rather than appending to the old one (that is why the filters are
 * part of the query key).
 *
 * `matched` and `discrepancy` are both post-match states; only the first can
 * be posted, so the status column is the primary triage signal on this screen.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent } from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell, moneyCell } from "@/components/data-table";

import { SUPPLIER_INVOICE_STATUSES, type SupplierInvoice } from "../api";
import { PageHeader, SelectField } from "../components/fields";
import { usePartyOptions, useSupplierInvoiceList, type SupplierInvoiceFilters } from "../queries";

const ALL = "__all__";

export const SUPPLIER_INVOICE_STATUS_VARIANTS = {
  draft: "outline",
  matched: "success",
  discrepancy: "warning",
  posted: "default",
  paid: "secondary",
  void: "destructive",
} as const;

export interface SupplierInvoicesPageProps {
  onOpenInvoice: (supplierInvoiceId: string) => void;
  onCaptureInvoice: () => void;
  canCapture?: boolean;
  canReadParties?: boolean;
  pageLimit?: number;
}

export function SupplierInvoicesPage({
  onOpenInvoice,
  onCaptureInvoice,
  canCapture = true,
  canReadParties = true,
  pageLimit,
}: SupplierInvoicesPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [partyId, setPartyId] = useState<string>(ALL);

  const filters = useMemo<SupplierInvoiceFilters>(() => {
    const built: SupplierInvoiceFilters = {};
    if (status !== ALL) {
      built.status = status as SupplierInvoice["status"];
    }
    if (partyId !== ALL) built.party_id = partyId;
    return built;
  }, [status, partyId]);

  const listQuery = useSupplierInvoiceList(filters, pageLimit);
  const partiesQuery = usePartyOptions("supplier", canReadParties);
  const parties = useMemo(() => partiesQuery.data ?? [], [partiesQuery.data]);
  const partyName = (id: string): string =>
    parties.find((party) => party.id === id)?.display_name ?? id;

  const statusLabel = (value: string): string =>
    t(`finance.supplierInvoices.status.${value}`, value);

  const columns = useMemo<ColumnDef<SupplierInvoice>[]>(
    () => [
      {
        accessorKey: "document_no",
        id: "document_no",
        header: t("finance.supplierInvoices.columns.documentNo", "Document"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "supplier_invoice_number",
        id: "supplier_invoice_number",
        header: t("finance.supplierInvoices.columns.supplierNumber", "Supplier reference"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "party_id",
        id: "party_id",
        header: t("finance.supplierInvoices.columns.supplier", "Supplier"),
        enableSorting: false,
        cell: (ctx) => partyName(ctx.getValue<string>()),
      },
      {
        accessorKey: "invoice_date",
        id: "invoice_date",
        header: t("finance.supplierInvoices.columns.invoiceDate", "Invoice date"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "due_date",
        id: "due_date",
        header: t("finance.supplierInvoices.columns.dueDate", "Due"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.supplierInvoices.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, SUPPLIER_INVOICE_STATUS_VARIANTS, statusLabel(value));
        },
      },
      {
        id: "open_findings",
        header: t("finance.supplierInvoices.columns.openFindings", "Open findings"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) =>
          // A count of findings is an integer, never money — safe in a number.
          String(
            ctx.row.original.discrepancies.filter((finding) => finding.status === "open").length,
          ),
      },
      {
        accessorKey: "grand_total",
        id: "grand_total",
        header: t("finance.supplierInvoices.columns.grandTotal", "Total"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => moneyCell(ctx.row.original.grand_total),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- name/label helpers close over stable query data
    [t, parties],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.supplierInvoices.title", "Supplier invoices")}
        description={t(
          "finance.supplierInvoices.subtitle",
          "Captured supplier invoices with their three-way-match verdict.",
        )}
        actions={
          canCapture ? (
            <Button type="button" onClick={onCaptureInvoice}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.supplierInvoices.capture", "Capture invoice")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="supplier-invoice-status"
            label={t("finance.supplierInvoices.filterStatus", "Status")}
            value={status}
            onValueChange={setStatus}
            className="w-52"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...SUPPLIER_INVOICE_STATUSES.map((value) => ({
                value,
                label: statusLabel(value),
              })),
            ]}
          />
          <SelectField
            id="supplier-invoice-party"
            label={t("finance.supplierInvoices.filterSupplier", "Supplier")}
            value={partyId}
            onValueChange={setPartyId}
            className="w-72"
            disabled={!canReadParties}
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...parties.map((party) => ({ value: party.id, label: party.display_name })),
            ]}
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        query={listQuery}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenInvoice(row.id)}
        storageKey="finance.supplierInvoices.columns"
        emptyMessage={t(
          "finance.supplierInvoices.empty",
          "No supplier invoices match these filters.",
        )}
      />
    </div>
  );
}
